import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { METRIC_KEYS, formatDate, metricsForPlatform } from "@/lib/format";
import { Uploader } from "@/components/portal/Uploader";
import { ScreenshotReader } from "@/components/portal/ScreenshotReader";
import {
  Button, ErrorNote, Field, Modal, Note, inputClass,
} from "@/components/portal/Ui";

const PLATFORMS = ["Instagram", "TikTok", "XiaoHongShu", "Threads"];

/** Admin correction of a KOL submission. Saved as a new version on top of the KOL's own entry. */
export function CorrectionModal({ post, onClose, onSaved }) {
  const insight = post?.latest_insight;
  const [form, setForm] = useState({});
  const [metrics, setMetrics] = useState({});
  const [unavailable, setUnavailable] = useState({});
  const [note, setNote] = useState("");
  const [kept, setKept] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [uploaderKey, setUploaderKey] = useState(0);

  useEffect(() => {
    if (!post) return;
    setForm({
      platform: post.platform,
      url: post.url,
      posting_date: post.posting_date,
      capture_date: insight?.capture_date || "",
    });
    const next = {};
    const flags = {};
    METRIC_KEYS.forEach((key) => {
      next[key] = insight?.[key] ?? "";
      flags[key] = (insight?.unavailable_metrics || []).includes(key);
    });
    setMetrics(next);
    setUnavailable(flags);
    setNote("");
    setKept(insight?.screenshots || []);
    setFiles([]);
    setError("");
    setUploaderKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  const fields = metricsForPlatform(form.platform);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (note.trim().length < 3) {
      setError("Note what you corrected and why — it is kept on the record.");
      return;
    }
    if (!form.capture_date) {
      setError("Enter the date these metrics were captured.");
      return;
    }
    const missing = fields.filter(
      (f) => !unavailable[f.key] && (metrics[f.key] === "" || metrics[f.key] == null)
    );
    if (missing.length > 0) {
      setError(
        `Enter a number for ${missing.map((f) => f.label).join(", ")}, or tick "Not available".`
      );
      return;
    }
    setBusy(true);
    setError("");
    const payload = {
      platform: form.platform,
      url: form.url,
      posting_date: form.posting_date,
      capture_date: form.capture_date,
      note: note.trim(),
      screenshot_file_ids: [...kept.map((f) => f.id), ...files.map((f) => f.id)],
      unavailable: fields.filter((f) => unavailable[f.key]).map((f) => f.key),
    };
    const applicable = new Set(fields.map((f) => f.key));
    METRIC_KEYS.forEach((key) => {
      const raw = applicable.has(key) && !unavailable[key] ? metrics[key] : null;
      payload[key] = raw === "" || raw == null ? null : Number(raw);
    });
    try {
      await api.post(`/posts/${post.id}/correction`, payload);
      toast.success("Correction saved — the KOL's original entry is kept in the history");
      await onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(post)}
      onClose={() => !busy && onClose()}
      title="Correct submission"
      wide
      testId="correction-modal"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="correction-form">
        <ErrorNote message={error} />
        <Note tone="neutral">
          The KOL's original entry is never overwritten — this is saved as a new version on top, and
          the row is marked "Adjusted by AnyTribe". Corrected figures are what campaign totals, the
          client dashboard and every export read.
        </Note>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Platform" required>
            <select
              value={form.platform || ""}
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
              className={inputClass}
              data-testid="correction-platform-select"
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Posting date" required>
            <input
              type="date"
              value={form.posting_date || ""}
              onChange={(e) => setForm({ ...form, posting_date: e.target.value })}
              className={inputClass}
              data-testid="correction-posting-date-input"
            />
          </Field>
        </div>
        <Field label="Post link" required>
          <input
            value={form.url || ""}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            className={inputClass}
            data-testid="correction-url-input"
          />
        </Field>
        <Field label="Metrics capture date" required>
          <input
            type="date"
            value={form.capture_date || ""}
            onChange={(e) => setForm({ ...form, capture_date: e.target.value })}
            className={inputClass}
            data-testid="correction-capture-date-input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {fields.map((field) => (
            <Field key={field.key} label={field.label} hint={field.hint}>
              <input
                type="number"
                min="0"
                step="1"
                disabled={Boolean(unavailable[field.key])}
                value={unavailable[field.key] ? "" : metrics[field.key] ?? ""}
                onChange={(e) => setMetrics((m) => ({ ...m, [field.key]: e.target.value }))}
                className={`${inputClass} disabled:bg-[#F4F1EB] disabled:text-[#8a8378]`}
                data-testid={`correction-${field.key}-input`}
              />
              <span className="mt-1 block text-[11px] body-text">
                KOL reported: {insight?.[field.key] == null ? "—" : insight[field.key]}
              </span>
              <label className="mt-1 flex items-center gap-1.5 text-xs body-text">
                <input
                  type="checkbox"
                  checked={Boolean(unavailable[field.key])}
                  onChange={(e) =>
                    setUnavailable((u) => ({ ...u, [field.key]: e.target.checked }))
                  }
                  data-testid={`correction-${field.key}-unavailable`}
                />
                Not available
              </label>
            </Field>
          ))}
        </div>

        <Field
          label="Screenshots"
          hint="Optional for a correction — untick to drop one, or upload replacements. A correction saved with no screenshot is marked internally as resting on your word."
        >
          <div className="space-y-2">
            {kept.map((file) => (
              <label
                key={file.id}
                className="flex items-center gap-2 text-sm"
                data-testid={`correction-keep-${file.id}`}
              >
                <input
                  type="checkbox"
                  checked
                  onChange={() => setKept((list) => list.filter((f) => f.id !== file.id))}
                />
                {file.original_filename}
              </label>
            ))}
            <Uploader
              key={uploaderKey}
              kind="screenshot"
              multiple
              files={files}
              onChange={setFiles}
              testId="correction-uploader"
            />
          </div>
        </Field>

        <ScreenshotReader
          fileIds={[...kept, ...files].map((f) => f.id)}
          platform={form.platform}
          testId="correction-reader"
          onRead={(data) => {
            setMetrics((current) => ({ ...current, ...data.values }));
            setUnavailable((current) => {
              const next = { ...current };
              Object.keys(data.values).forEach((key) => delete next[key]);
              return next;
            });
            if (data.capture_date) setForm((current) => ({ ...current, capture_date: data.capture_date }));
          }}
        />

        <Field label="What did you correct, and why?" required>
          <textarea
            rows={2}
            required
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputClass}
            placeholder="e.g. Reach read from the screenshot was the 7-day figure; corrected to lifetime."
            data-testid="correction-note-input"
          />
        </Field>

        <p className="text-xs body-text">
          KOL's entry: {insight?.submitted_by_name || "—"}
          {insight?.capture_date ? ` · captured ${formatDate(insight.capture_date)}` : ""}
        </p>

        <div className="flex justify-end gap-2.5 pt-1">
          <Button variant="outline" onClick={onClose} disabled={busy} testId="correction-cancel">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="correction-submit">
            Save correction
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Quiet marker shown wherever a corrected submission appears internally. */
export function AdjustedBadge({ post, testId }) {
  const [open, setOpen] = useState(false);
  if (!post?.is_adjusted) return null;
  const changes = post.adjustment_changes || [];
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className="inline-flex items-center rounded-full bg-[#EDF2FB] px-2 py-0.5 text-[11px] font-bold text-[#2C4D8F] hover:bg-[#DFE8F8]"
      >
        Adjusted by AnyTribe
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="What AnyTribe corrected"
        testId={`${testId}-modal`}
      >
        <div className="space-y-3">
          <p className="text-sm body-text">
            {post.adjusted_by_name} · {formatDate(post.adjusted_at)}
          </p>
          {post.adjustment_note && <Note tone="neutral">{post.adjustment_note}</Note>}
          {post.adjustment_without_screenshot && (
            <p
              className="text-sm font-semibold text-[#A85A05]"
              data-testid={`${testId}-no-screenshot`}
            >
              Saved with no screenshot — these figures rest on the admin&apos;s verification, not on
              uploaded proof.
            </p>
          )}
          {changes.length === 0 ? (
            <p className="text-sm body-text">No field values changed in the last correction.</p>
          ) : (
            <ul className="space-y-1.5 text-sm" data-testid={`${testId}-changes`}>
              {changes.map((change) => (
                <li key={change.field} className="flex flex-wrap gap-1.5">
                  <span className="font-semibold capitalize">{change.field}:</span>
                  <span className="body-text">{String(change.from ?? "—")}</span>
                  <span aria-hidden>→</span>
                  <span className="font-semibold">{String(change.to ?? "—")}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs body-text">
            The KOL's original entry stays in the insight history alongside this correction.
          </p>
        </div>
      </Modal>
    </>
  );
}
